import { useCreateOrganization, useUserInfo } from "@/api/user";
import { ApiError } from "@/api/utils";
import { PENDING_BETA_INVITE_CODE_KEY } from "@/constants/betaInvite";
import { ROUTES } from "@/constants/routes";
import { CenteredLayout } from "@/layouts/CenteredLayout";
import { nativeLoginOpts } from "@/native/auth";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import * as analytics from "@/utils/analytics";
import { useAuth0 } from "@auth0/auth0-react";
import type { CreateOrganizationPayload } from "@cohesive/api-client";
import {
  clearSignupUtm,
  consumeTemplateSignupIntent,
  parseSignupUtm,
  parseTemplateSignupIntent,
  peekSignupUtm,
  Spinner,
} from "@cohesive/ui-components";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

/** `ZenseiApiErrorBody` plus the machine-readable refusal code. */
type WorkspaceSetupErrorBody = {
  error?: { message?: string; code?: string };
};

// Outlives the component: unmounting mid-flight does not cancel the create, and
// the org-less bounce remounts this page. Module-scoped so a remount cannot mint
// a second org while the first POST is still in the air. Success latches it for
// the life of the document: loginWithRedirect resolves before the unload, so a
// Back press in that window would otherwise remount with the guard down and mint
// a second org. Only an error unlatches it.
let provisioning = false;

export function __resetWorkspaceSetupGuardForTests() {
  provisioning = false;
}

export function WorkspaceSetupPage() {
  const navigate = useNavigate();
  const { user, loginWithRedirect } = useAuth0();
  const { data: userInfo, isError } = useUserInfo();
  const startedRef = useRef(false);

  const createOrganizationMutation = useCreateOrganization({
    onSuccess: async (organization, payload) => {
      clearSignupUtm();
      analytics.trackOrganizationCreated({
        organizationId: organization.id,
        signupSource: payload.signup_source,
        utm: payload.utm,
      });

      await loginWithRedirect({
        authorizationParams: { organization: organization.auth0_id },
        appState: { returnTo: ROUTES.HOME },
        ...nativeLoginOpts(),
      });
    },
    onError: (error) => {
      // v5 routes an onSuccess throw here too, so a rejected redirect unlatches.
      provisioning = false;

      // Flag-off and the per-IP cap are expected refusals, not faults; both fall
      // back to the manual form. Only surface a message we didn't ask for.
      const apiError =
        error instanceof ApiError
          ? (error.data as WorkspaceSetupErrorBody | undefined)?.error
          : undefined;
      const refused = apiError?.code === "signup_source_refused";
      if (!refused && apiError?.message) {
        toast.error(apiError.message);
      }
      navigate(ROUTES.ONBOARDING.CREATE_ORGANIZATION, { replace: true });
    },
  });

  useEffect(() => {
    if (startedRef.current) return;

    // Only a successful, empty `/me` means this user has no org. An error tells
    // us nothing, and provisioning on it mints a second org for an existing
    // account that came back through `/signup`.
    if (isError) {
      startedRef.current = true;
      navigate(ROUTES.ONBOARDING.CREATE_ORGANIZATION, { replace: true });
      return;
    }
    if (!userInfo) return;

    startedRef.current = true;

    // Consume unconditionally: whichever branch wins, the stash is spent.
    const stashed = consumeTemplateSignupIntent();

    if ((userInfo.organizations?.length ?? 0) > 0) {
      navigate(ROUTES.HOME, { replace: true });
      return;
    }

    // Invite codes ride the org-name form, which posts the code with the name:
    // the backend refuses a silent signup source combined with a code.
    const pendingInviteCode =
      typeof sessionStorage === "undefined"
        ? null
        : sessionStorage.getItem(PENDING_BETA_INVITE_CODE_KEY);
    if (pendingInviteCode) {
      navigate(ROUTES.ONBOARDING.CREATE_ORGANIZATION, { replace: true });
      return;
    }

    // An earlier mount's create is still in flight; its callbacks carry the
    // user forward, so this one only shows the spinner.
    if (provisioning) return;

    const intent = parseTemplateSignupIntent(window.location.search) ?? stashed;
    const utm = parseSignupUtm(window.location.search) ?? peekSignupUtm();

    const payload: CreateOrganizationPayload = intent
      ? {
          signup_source: "public_template",
          billing_email: user?.email,
          template_prompt: {
            kind: intent.kind,
            id: intent.id,
            title: intent.title,
            author_name: intent.author,
            cta: intent.cta,
            created_at: new Date(intent.ts ?? Date.now()).toISOString(),
          },
          ...(utm ? { utm } : {}),
        }
      : {
          signup_source: "self_serve",
          billing_email: user?.email,
          ...(utm ? { utm } : {}),
        };

    provisioning = true;
    createOrganizationMutation.mutate(payload);
  }, [isError, userInfo, user, navigate, createOrganizationMutation]);

  return (
    <>
      <title>Setting up your workspace</title>

      <CenteredLayout maxWidth="md">
        <div className="space-y-8 text-center">
          <div className="flex justify-center">
            <LogoIcon width="64" height="64" />
          </div>
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground tracking-medium">
              Setting up your workspace
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium tracking-tight">
              This only takes a moment.
            </p>
          </div>
          <div
            data-testid="workspace-setup-spinner"
            className="flex justify-center"
          >
            <Spinner size="xl" />
          </div>
        </div>
      </CenteredLayout>
    </>
  );
}
